import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

// logic-specific imports
import { Component, OnInit, Inject, Optional } from '@angular/core';
import { QuickBuildService } from 'src/app/people/quick-build.service';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';
import { RoleTitlesService } from 'src/app/core/services/role-titles.service';
import { DeleteConfirmationComponent } from '../components/delete-confirmation/delete-confirmation.component';
import { Log } from 'src/app/logger.service';
import { NotificationService } from '../notification-service/notification.service';

@Component({
    selector: 'app-role-builder',
    templateUrl: './role-builder.component.html',
    providers: [
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    ],
    standalone: false
})
export class RoleBuilderComponent implements OnInit {
  Math = Math;

  matchingTitles: { title: string; peopleCount: number }[] = [];
  totalPeople = 0;
  isLoadingTitles = false;

  // Maximum number of values per criteria field
  readonly MAX_VALUES_PER_CRITERIA = 20;

  private originalRole: RoleDefinition | null = null;
  private originalFormValue: any = null;

  constructor(
    private dialogRef: MatDialogRef<RoleBuilderComponent>,
    private fb: FormBuilder,
    private roleDefinitionService: RoleDefinitionsService,
    private quickBuildService: QuickBuildService,
    private roleTitlesService: RoleTitlesService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { role?: RoleDefinition }
  ) {}

  ngOnInit() {
    // Initialize with all job titles
    this.matchingTitles = [];

    // If an existing Role is provided, prefill the form
    if (this.data?.role) {
      const role = this.data.role;
      this.originalRole = role;
      this.roleForm.patchValue(
        {
          name: role.name ?? '',
          description: role.description ?? '',
          category: role.category ?? '',
          criteria: {
            mustExact: role.criteria.mustExact ?? [],
            mustFuzzy: role.criteria.mustFuzzy ?? [],
            mustNotExact: role.criteria.mustNotExact ?? [],
            mustNotFuzzy: role.criteria.mustNotFuzzy ?? [],
          },
        },
        { emitEvent: false }
      );
    }

    // Store the original form value for comparison
    this.originalFormValue = this.roleForm.getRawValue();

    // Subscribe to form changes to update matching titles
    this.roleForm.get('criteria')?.valueChanges.subscribe(() => {
      this.updateMatchingTitles();
    });

    // Initial filtering (in case form has default values)
    this.updateMatchingTitles();
  }

  separatorKeysCodes: number[] = [ENTER, COMMA];

  roleForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    description: this.fb.control<string | null>(''),
    category: this.fb.control<string | null>(''),
    criteria: this.fb.group({
      mustExact: [[] as string[]],
      mustFuzzy: [[] as string[]],
      mustNotExact: [[] as string[]],
      mustNotFuzzy: [[] as string[]],
    }),
  });

  updateMatchingTitles() {
    const { name, criteria } = this.roleForm.getRawValue();
    const safeCriteria = {
      mustExact: (criteria?.mustExact as string[]) || [],
      mustFuzzy: (criteria?.mustFuzzy as string[]) || [],
      mustNotExact: (criteria?.mustNotExact as string[]) || [],
      mustNotFuzzy: (criteria?.mustNotFuzzy as string[]) || [],
    };

    this.isLoadingTitles = true;
    this.roleTitlesService
      .getMatchingTitlesWithStats(safeCriteria, name || 'RoleBuilder', 50)
      .subscribe({
        next: (result) => {
          // Use API response order (sorted by count by default)
          this.matchingTitles = result.titles;
          this.totalPeople = result.totalPeople;
          this.isLoadingTitles = false;
        },
        error: () => {
          this.matchingTitles = [];
          this.totalPeople = 0;
          this.isLoadingTitles = false;
        },
      });
  }

  // these are used by template chips
  get mustExact() {
    return this.roleForm.get('criteria.mustExact') as FormControl<string[]>;
  }
  get mustFuzzy() {
    return this.roleForm.get('criteria.mustFuzzy') as FormControl<string[]>;
  }
  get mustNotExact() {
    return this.roleForm.get('criteria.mustNotExact') as FormControl<string[]>;
  }
  get mustNotFuzzy() {
    return this.roleForm.get('criteria.mustNotFuzzy') as FormControl<string[]>;
  }

  // Form state checks
  get hasFormChanged(): boolean {
    if (!this.originalFormValue) return false;
    const currentValue = this.roleForm.getRawValue();
    return JSON.stringify(currentValue) !== JSON.stringify(this.originalFormValue);
  }

  get hasNameChanged(): boolean {
    if (!this.originalRole) return true; // For new roles, name is always "changed"
    const currentName = this.roleForm.get('name')?.value?.trim() || '';
    return currentName !== this.originalRole.name;
  }

  get canSave(): boolean {
    return this.roleForm.valid && (!this.originalRole || this.hasFormChanged);
  }

  get canSaveAsNew(): boolean {
    return this.roleForm.valid && this.originalRole !== null && this.hasNameChanged;
  }

  add(event: MatChipInputEvent, control: FormControl<string[]>): void {
    const value = (event.value || '').trim();
    if (value) {
      // Split by newlines and filter out empty values
      const values = value
        .split(/\r?\n/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      // Add all non-empty values to the control, respecting the max limit
      if (values.length > 0) {
        const currentValues = control.value || [];
        const remainingSlots = Math.max(0, this.MAX_VALUES_PER_CRITERIA - currentValues.length);

        if (remainingSlots === 0) {
          // Already at max capacity
          return;
        }

        const valuesToAdd = values.slice(0, remainingSlots);
        control.setValue([...currentValues, ...valuesToAdd]);

        // Alert user if some values were not added
        if (values.length > remainingSlots) {
          const notAdded = values.length - remainingSlots;
          this.notificationService.warning(
            `Maximum of ${this.MAX_VALUES_PER_CRITERIA} values allowed. ${notAdded} value(s) were not added.`
          );
        }
      }
    }
    event.chipInput!.clear();
  }

  remove(keyword: string, control: FormControl<string[]>): void {
    const index = control.value.indexOf(keyword);
    if (index >= 0) {
      const newValue = [...control.value];
      newValue.splice(index, 1);
      control.setValue(newValue);
    }
  }

  onPaste(event: ClipboardEvent, control: FormControl<string[]>): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';

    if (clipboardData.trim()) {
      // Split by both newlines and commas, then filter out empty values
      const values = clipboardData
        .split(/[\r\n,]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      // Add all non-empty values to the control, respecting the max limit
      if (values.length > 0) {
        const currentValues = control.value || [];
        const remainingSlots = Math.max(0, this.MAX_VALUES_PER_CRITERIA - currentValues.length);

        if (remainingSlots === 0) {
          this.notificationService.warning(
            `Maximum of ${this.MAX_VALUES_PER_CRITERIA} values allowed. No values can be added.`
          );
          return;
        }

        const valuesToAdd = values.slice(0, remainingSlots);
        control.setValue([...currentValues, ...valuesToAdd]);

        // Alert user if some values were not added
        if (values.length > remainingSlots) {
          const notAdded = values.length - remainingSlots;
          this.notificationService.warning(
            `Maximum of ${this.MAX_VALUES_PER_CRITERIA} values allowed. Added ${remainingSlots}, skipped ${notAdded}.`
          );
        }
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCreate(): void {
    if (this.roleForm.invalid) {
      this.roleForm.markAllAsTouched();
      return;
    }
    const formValue = this.roleForm.getRawValue();
    const roleToSave: Partial<RoleDefinition> = {
      name: formValue.name.trim(),
      description: formValue.description?.trim() || '',
      category: formValue.category?.trim() || undefined,
      criteria: {
        mustExact: formValue.criteria.mustExact || [],
        mustFuzzy: formValue.criteria.mustFuzzy || [],
        mustNotExact: formValue.criteria.mustNotExact || [],
        mustNotFuzzy: formValue.criteria.mustNotFuzzy || [],
      },
    };
    this.dialogRef.close(roleToSave);
  }

  onSaveAsNew(): void {
    if (this.roleForm.invalid) {
      this.roleForm.markAllAsTouched();
      return;
    }
    const formValue = this.roleForm.getRawValue();
    const roleToSave: Partial<RoleDefinition> = {
      name: formValue.name.trim(),
      description: formValue.description?.trim() || '',
      category: formValue.category?.trim() || undefined,
      criteria: {
        mustExact: formValue.criteria.mustExact || [],
        mustFuzzy: formValue.criteria.mustFuzzy || [],
        mustNotExact: formValue.criteria.mustNotExact || [],
        mustNotFuzzy: formValue.criteria.mustNotFuzzy || [],
      },
    };
    this.dialogRef.close({ ...roleToSave, saveAsNew: true });
  }

  onDelete(): void {
    if (!this.data?.role) return;

    const confirmDialog = this.dialog.open(DeleteConfirmationComponent, {
      data: {
        title: 'Delete Role',
        message: `Are you sure you want to delete the role "${this.data.role.name}"? This action cannot be undone.`,
      },
    });

    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result === true && this.data?.role) {
        this.roleDefinitionService.delete(this.data.role.id).subscribe({
          next: () => {
            this.dialogRef.close({ deleted: true });
          },
          error: (error) => {
            Log.s('RoleBuilderComponent - error deleting role', 'error', {
              error: error,
              roleId: this.data?.role?.id || 'unknown',
            });
          },
        });
      }
    });
  }
}
